import { useEffect, useState, useCallback, useRef } from "react";
import RichBody from "@/components/site/RichBody";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { mediaUrl, embedSourceUrl } from "@/lib/api";
import { toEmbedUrl, embedThumbnail } from "@/pages/Home";

/**
 * MediaLightbox — a full-screen viewer visitors can use to browse items and choose what to view.
 *
 * Props:
 *   items         array of content items (image | video | production | article | ad)
 *   startIndex    the index the lightbox should open at
 *   onClose       callback fired when the user closes the lightbox
 *   testIdPrefix  optional prefix for data-testids (default 'lightbox')
 */
export default function MediaLightbox({ items = [], startIndex = 0, onClose, testIdPrefix = "lightbox" }) {
  const [index, setIndex] = useState(startIndex);
  const [playing, setPlaying] = useState(false);
  const touchStartX = useRef(null);

  const total = items.length;
  const item = items[index];

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);

  // Auto-advance slideshow (pauses automatically on video/embed items — controls are handled by the player itself)
  useEffect(() => {
    if (!playing || total < 2) return;
    const cur = items[index];
    if (!cur) return;
    // Don't tick while a video/embed is being watched
    const hasVideo = (cur.type === "video" || cur.type === "inspiration") ? (cur.media_id || cur.video_id) : cur.video_id;
    if (hasVideo || embedSourceUrl(cur)) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % total), 4500);
    return () => clearInterval(t);
  }, [playing, index, items, total]);

  // Keyboard nav + body-scroll lock
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose]);

  if (!item) return null;

  // Rich viewer: any content type can carry image + video + embed + article body.
  // Priority: uploaded video > embed URL > image > article-only.
  const uploadedVideoId = (item.type === "video" || item.type === "inspiration") ? (item.media_id || item.video_id) : item.video_id;
  const videoSrc = uploadedVideoId ? mediaUrl({ media_id: uploadedVideoId }) : null;
  const embed = embedSourceUrl(item) ? toEmbedUrl(embedSourceUrl(item)) : null;
  const posterSrc = item.thumb_id ? mediaUrl({ thumb_id: item.thumb_id }) : (item.external_url || null);
  // For image src, prefer media_id/thumb_id/external unless media_id points to the video itself
  const imageSrc = (item.type === "video" || item.type === "inspiration") ? posterSrc : mediaUrl(item);
  const hasMedia = !!(videoSrc || embed || imageSrc);
  const hasBody = !!item.body;
  const isArticleLayout = item.type === "article" || (hasBody && !videoSrc && !embed);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 60) { if (dx < 0) next(); else prev(); }
    touchStartX.current = null;
  };

  const node = (
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[300] bg-imi-bg/95 backdrop-blur-md flex flex-col"
        data-testid={testIdPrefix}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-imi-border/40">
          <div className="text-imi-text/60 text-[11px] uppercase tracking-[0.25em]">
            <span className="text-imi-gold">{index + 1}</span> / {total}
            {item.category && <span className="ml-3 text-imi-text/40">· {item.category}</span>}
          </div>
          <div className="flex items-center gap-2">
            {total > 1 && (
              <button
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "Pause slideshow" : "Play slideshow"}
                data-testid={`${testIdPrefix}-slideshow`}
                title={playing ? "Pause slideshow" : "Play slideshow"}
                className={`h-10 px-3 rounded-full border text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 transition-colors ${playing ? "border-imi-gold text-imi-gold bg-imi-gold/10" : "border-imi-border text-imi-text/60 hover:border-imi-gold hover:text-imi-gold"}`}
              >
                {playing ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Slideshow</>}
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid={`${testIdPrefix}-close`}
              className="h-10 w-10 rounded-full border border-imi-border hover:border-imi-gold hover:text-imi-gold text-imi-text/60 flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Media stage */}
        <div className="flex-1 relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex items-center justify-center p-4 md:p-10"
            >
              {videoSrc ? (
                <video
                  src={videoSrc} poster={posterSrc || undefined}
                  controls autoPlay playsInline preload="metadata"
                  className="max-w-full max-h-full object-contain border border-imi-border"
                />
              ) : embed ? (
                <div className="w-full max-w-5xl aspect-video border border-imi-border">
                  <iframe src={`${embed}${embed.includes("?") ? "&" : "?"}autoplay=1&mute=1`} title={item.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
                </div>
              ) : isArticleLayout ? (
                <div className={`w-full max-w-3xl mx-auto max-h-full overflow-y-auto py-4 ${!imageSrc && !hasBody && item.type === "article" ? "text-center" : ""}`}>
                  {imageSrc && <img src={imageSrc} alt={item.title} className="w-full aspect-[16/9] object-cover border border-imi-border mb-8" />}
                  <p className="text-[11px] uppercase tracking-[0.25em] text-imi-gold mb-4">{item.category || "Editorial"}</p>
                  <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight text-imi-text">{item.title}</h2>
                  {item.excerpt && <p className={`mt-4 text-imi-text/70 text-lg leading-relaxed ${!imageSrc && !hasBody && item.type === "article" ? "max-w-2xl mx-auto" : ""}`}>{item.excerpt}</p>}
                  {item.body && <RichBody text={item.body} className="mt-6" paragraphClass="text-imi-text/80 leading-relaxed whitespace-pre-line" testId="lightbox-body" />}
                  {item.cta_url && (
                    <a href={item.cta_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`${testIdPrefix}-read-full`}
                       className="mt-8 inline-flex items-center gap-2 bg-imi-gold text-imi-bg px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-imi-goldlight transition-colors">
                      Read the full article <ChevronRight size={16} />
                    </a>
                  )}
                </div>
              ) : imageSrc ? (
                <div className="w-full max-w-5xl max-h-full flex flex-col items-center">
                  <img src={imageSrc} alt={item.title} className="max-w-full max-h-[70vh] object-contain" />
                  {hasBody && (
                    <RichBody text={item.body} className="mt-6 max-w-3xl px-4 overflow-y-auto" paragraphClass="text-imi-text/70 text-base leading-relaxed whitespace-pre-line" testId="lightbox-body" />
                  )}
                </div>
              ) : (
                <div className="text-imi-text/40 flex items-center gap-3"><Play size={32} /> No preview available</div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Prev/Next arrows */}
          {total > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous"
                data-testid={`${testIdPrefix}-prev`}
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-imi-gold/40 bg-imi-bg/60 backdrop-blur text-imi-gold hover:bg-imi-gold hover:text-imi-bg transition-colors flex items-center justify-center"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={next}
                aria-label="Next"
                data-testid={`${testIdPrefix}-next`}
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full border border-imi-gold/40 bg-imi-bg/60 backdrop-blur text-imi-gold hover:bg-imi-gold hover:text-imi-bg transition-colors flex items-center justify-center"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </div>

        {/* Caption + thumbnail rail */}
        <div className="border-t border-imi-border/40 bg-imi-surface/60 backdrop-blur">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
            <div className="flex items-baseline justify-between gap-4 mb-3">
              <h3 className="font-serif text-lg md:text-2xl text-imi-text truncate">{item.title}</h3>
              {item.meta && <span className="text-imi-text/40 text-xs shrink-0">{item.meta}</span>}
            </div>
            {!isArticleLayout && item.excerpt && (
              <p className="text-imi-text/60 text-sm max-w-3xl line-clamp-2 mb-4">{item.excerpt}</p>
            )}
            {total > 1 && (
              <div className="flex gap-2 overflow-x-auto py-2" style={{ scrollbarWidth: "none" }} data-testid={`${testIdPrefix}-rail`}>
                {items.map((it, idx) => {
                  const thumb = (it.thumb_id ? mediaUrl({ thumb_id: it.thumb_id }) : mediaUrl(it)) || embedThumbnail(embedSourceUrl(it));
                  return (
                    <button
                      key={it.id}
                      onClick={() => setIndex(idx)}
                      data-testid={`${testIdPrefix}-thumb-${idx}`}
                      className={`relative shrink-0 w-20 h-14 border overflow-hidden transition-all ${idx === index ? "border-imi-gold ring-2 ring-imi-gold/40" : "border-imi-border opacity-60 hover:opacity-100"}`}
                      aria-label={`Go to item ${idx + 1}`}
                    >
                      {thumb ? (
                        <img src={thumb} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-imi-elevated flex items-center justify-center text-imi-text/30 text-[10px]">No image</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(node, document.body);
}
